/* Progressive enhancement for native Shopify forms. No catalogue data is stored here. */
(() => {
  'use strict';
  const root = () => window.Shopify?.routes?.root || '/';
  const message = (element, text, error = false) => {
    if (!element) return;
    element.textContent = text;
    element.classList.toggle('is-error', error);
  };
  const request = async (url, options) => {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let body;
    try { body = await response.json(); }
    catch (_) { throw new Error('La boutique ne répond pas correctement. Réessayez dans un instant.'); }
    if (!response.ok || body.status >= 400) throw new Error(body.description || body.message || 'Cette modification est impossible pour le moment. Réessayez.');
    return body;
  };
  const initProduct = section => {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';
    const select = section.querySelector('[data-variant-select]');
    const form = section.querySelector('[data-product-form]');
    const add = section.querySelector('[data-add-button]');
    const quantity = form?.querySelector('[name="quantity"]');
    const status = section.querySelector('[data-product-message]');
    const enabled = section.dataset.salesEnabled === 'true';
    const media = [...section.querySelectorAll('[data-media-id].product-gallery__item')];
    const thumbnails = [...section.querySelectorAll('[data-select-media]')];
    const showMedia = id => {
      if (!media.some(item => item.dataset.mediaId === String(id))) return;
      media.forEach(item => {
        const active = item.dataset.mediaId === String(id);
        item.hidden = !active;
        item.classList.toggle('is-active', active);
        if (!active) item.querySelectorAll('video').forEach(video => video.pause());
      });
      thumbnails.forEach(item => {
        const active = item.dataset.selectMedia === String(id);
        item.classList.toggle('is-active', active);
        if (active) item.setAttribute('aria-current', 'true'); else item.removeAttribute('aria-current');
      });
    };
    showMedia(media.find(item => item.classList.contains('is-active'))?.dataset.mediaId || media[0]?.dataset.mediaId);
    thumbnails.forEach(link => link.addEventListener('click', event => { event.preventDefault(); showMedia(link.dataset.selectMedia); }));
    const updateVariant = (updateURL = true) => {
      const option = select?.selectedOptions[0];
      if (!option) return;
      const price = section.querySelector('[data-product-price]');
      const compare = section.querySelector('[data-product-compare]');
      const unit = section.querySelector('[data-product-unit-price]');
      if (price) price.textContent = option.dataset.price;
      if (compare) { compare.textContent = option.dataset.compare; compare.hidden = !option.dataset.compare; }
      if (unit) { unit.textContent = option.dataset.unitPrice; unit.hidden = !option.dataset.unitPrice; }
      const available = option.dataset.available === 'true';
      message(section.querySelector('[data-product-availability]'), available ? 'Disponible' : 'Cette version est indisponible.');
      if (add) {
        add.disabled = !enabled || !available;
        add.textContent = !enabled ? 'Ouverture à venir' : !available ? 'Indisponible' : 'Ajouter au panier';
      }
      if (quantity) {
        quantity.min = option.dataset.min || '1';
        quantity.step = option.dataset.step || '1';
        if (option.dataset.max) quantity.max = option.dataset.max; else quantity.removeAttribute('max');
        quantity.value = quantity.min;
      }
      if (option.dataset.mediaId) showMedia(option.dataset.mediaId);
      message(status, '');
      if (updateURL) { const url = new URL(location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url); }
    };
    select?.addEventListener('change', () => updateVariant());
    updateVariant(false);
    form?.addEventListener('submit', async event => {
      event.preventDefault();
      if (!enabled || add?.disabled || !form.reportValidity()) return;
      add.disabled = true;
      add.setAttribute('aria-busy', 'true');
      message(status, 'Ajout en cours…');
      try {
        await request(`${root()}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
        location.assign(section.dataset.cartUrl || `${root()}cart`);
      } catch (error) {
        message(status, error.message || 'L’ajout n’a pas abouti. Vérifiez votre connexion puis réessayez.', true);
        add.disabled = false;
      } finally { add.removeAttribute('aria-busy'); }
    });
    const dialog = section.querySelector('[data-zoom-dialog]');
    let returnFocus;
    if (dialog?.showModal) {
      section.querySelectorAll('[data-zoom-image]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault();
        returnFocus = link;
        const img = dialog.querySelector('[data-zoom-target]');
        img.src = link.dataset.zoomImage;
        img.alt = link.querySelector('img')?.alt || '';
        dialog.showModal();
      }));
      dialog.querySelector('[data-close-zoom]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) { const bounds = dialog.getBoundingClientRect(); if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close(); } });
      dialog.addEventListener('close', () => returnFocus?.focus());
    }
  };
  const initCart = section => {
    const form = section.querySelector('[data-cart-form]');
    if (!form || form.dataset.initialized) return;
    form.dataset.initialized = 'true';
    const status = form.querySelector('[data-cart-message]');
    const checkout = form.querySelector('[data-checkout-button]');
    try {
      const flash = sessionStorage.getItem('studio-led-cart-error');
      if (flash) { message(status, flash, true); sessionStorage.removeItem('studio-led-cart-error'); }
    } catch (_) { /* Storage can be unavailable in privacy mode. */ }
    const update = async updates => {
      form.setAttribute('aria-busy', 'true');
      form.querySelectorAll('button').forEach(button => { button.dataset.wasDisabled = String(button.disabled); button.disabled = true; });
      message(status, 'Mise à jour en cours…');
      try {
        // change.js validates inventory. update.js does not validate existing line quantities.
        for (const [id, quantity] of Object.entries(updates).reverse()) {
          await request(`${root()}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id, quantity }) });
        }
        location.reload();
      } catch (error) {
        const errorText = `${error.message || 'Votre panier n’a pas pu être mis à jour.'} Vérifiez les quantités affichées avant de poursuivre.`;
        message(status, errorText, true);
        form.querySelectorAll('button').forEach(button => { button.disabled = button.dataset.wasDisabled === 'true'; });
        form.removeAttribute('aria-busy');
        // Reload authoritative totals after any partial update; preserve the explanation.
        try { sessionStorage.setItem('studio-led-cart-error', errorText); location.reload(); }
        catch (_) { if (checkout) checkout.disabled = true; }
      }
    };
    form.addEventListener('submit', event => {
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      if (form.getAttribute('aria-busy') === 'true' || !form.reportValidity()) return;
      const updates = {};
      form.querySelectorAll('input[data-cart-key]').forEach(input => { if (input.value !== input.defaultValue) updates[input.dataset.cartKey] = Number(input.value); });
      update(updates);
    });
    form.querySelectorAll('[data-cart-remove]').forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      if (form.getAttribute('aria-busy') !== 'true') update({ [link.dataset.cartKey]: 0 });
    }));
  };
  const initRecommendations = async element => {
    if (element.dataset.initialized || element.children.length || !element.dataset.url) return;
    element.dataset.initialized = 'true';
    try {
      const response = await fetch(element.dataset.url, { credentials: 'same-origin' });
      if (!response.ok) return;
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const recommendations = document.querySelector('[data-product-recommendations]');
      if (recommendations?.innerHTML.trim()) element.innerHTML = recommendations.innerHTML;
    } catch (_) { /* Optional recommendations remain absent when Shopify is unavailable. */ }
  };
  const init = (scope = document) => {
    scope.querySelectorAll('[data-product-section]').forEach(initProduct);
    scope.querySelectorAll('[data-cart-section]').forEach(initCart);
    scope.querySelectorAll('[data-product-recommendations]').forEach(element => {
      if ('IntersectionObserver' in window) { const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); initRecommendations(element); } }, { rootMargin: '200px' }); observer.observe(element); }
      else initRecommendations(element);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init()); else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
